import {
  LogoutOutlined,
  MoreOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import VisibilityIcon from '@mui/icons-material/Visibility'
import React, { useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Popover } from 'sutra/components/Popover/Popover'
import { UsernameInitials } from 'sutra/components/UsernameInitials/UsernameInitials'
import { getInitials } from 'sutra/utils/get-initials'

import { navDefaultIconColor } from 'assets/styles/variables'
import { AuthContext, useSignoutConfirmation } from 'features/auth'
import { useProjectLayoutState } from 'features/project/partials/project-layout/hooks/use-project-layout-state'
import { SETTINGS_URL } from 'features/settings/paths'
import { useCanAccess } from 'src/hooks/use-can-access'
import { ClientAuthPolicy } from 'src/types/auth-role'
import mixpanel from 'trackers/mixpanel'

type Props = {
  isCollapsed?: boolean
}

export const UserMenuButton: React.FC<Props> = ({ isCollapsed = false }) => {
  const [open, setOpen] = useState(false)

  // global state
  const { projectId, feature_flags } = useProjectLayoutState()

  // hooks
  const authContext = useContext(AuthContext)
  const navigate = useNavigate()
  const location = useLocation()
  const confirmSignout = useSignoutConfirmation()
  const appHostname = import.meta.env.VITE_APP_HOSTNAME.replace(/\/$/, '')

  // user data
  const firstName =
    authContext.userDetails?.first_name ||
    authContext.user?.attributes?.given_name ||
    ''
  const lastName =
    authContext.userDetails?.last_name ||
    authContext.user?.attributes?.family_name ||
    ''
  const email =
    authContext.userDetails?.email || authContext.user?.attributes?.email || ''
  const initials = getInitials({ firstName, lastName, email })

  // constants
  const projectBasePath = `/project/${projectId}`

  const canViewSettingsPage = useCanAccess(
    ClientAuthPolicy.ONLY_AUTHENTICATED_USERS,
  )
  const canSignout = useCanAccess(ClientAuthPolicy.ONLY_AUTHENTICATED_USERS)

  // Carry non-filter query params to settings/profile pages
  const searchParams = new URLSearchParams(location.search)
  searchParams.delete('filters')
  const searchStr = searchParams.toString()
  const search = searchStr ? `?${searchStr}` : ''

  // handlers
  const handleSignOut = (): void => {
    setOpen(false)
    confirmSignout()
  }

  const handleCopyShareLink = (): void => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.set('share_type', 'insights')
    searchParams.set('share_user', email)
    const url = `${appHostname}${window.location.pathname}?${searchParams.toString()}`
    navigator.clipboard.writeText(url).then(() => {
      toast.info('Copied to clipboard')
      setOpen(false)
    })
  }

  const handleNavigateToSettings = (): void => {
    navigate(`${projectBasePath}/${SETTINGS_URL}${search}`)
    mixpanel.track('settings', { proj_uuid: projectId })
    setOpen(false)
  }

  // Build menu items based on permissions and feature flags
  const menuItems: Array<{
    key: string
    label: string
    icon: React.ReactNode
    onClick: () => void
    show: boolean
  }> = [
    {
      key: 'settings',
      label: 'Settings',
      icon: <SettingOutlined />,
      onClick: handleNavigateToSettings,
      show: canViewSettingsPage,
    },
    {
      key: 'copy-share-link',
      label: 'Copy tracking link',
      icon: <VisibilityIcon style={{ fontSize: '16px' }} />,
      onClick: handleCopyShareLink,
      show: !!(feature_flags as Record<string, boolean> | undefined)?.[
        'copy_share_link'
      ],
    },
    {
      key: 'signout',
      label: 'Signout',
      icon: <LogoutOutlined />,
      onClick: handleSignOut,
      show: canSignout,
    },
  ]

  const visibleMenuItems = menuItems.filter((item) => item.show)

  const content = (
    <MenuList>
      {visibleMenuItems.map((item) => (
        <MenuItem key={item.key} onClick={item.onClick}>
          <MenuIcon>{item.icon}</MenuIcon>
          <MenuLabel>{item.label}</MenuLabel>
        </MenuItem>
      ))}
    </MenuList>
  )

  const triggerButton = (
    <TriggerButton $isCollapsed={isCollapsed}>
      <UsernameInitials initials={initials} size="md" />
      {!isCollapsed && (
        <UserInfo>
          <UserName>{`${firstName} ${lastName}`}</UserName>
          <UserEmail>{email}</UserEmail>
        </UserInfo>
      )}
      {!isCollapsed && (
        <MenuDotsIcon>
          <MoreOutlined />
        </MenuDotsIcon>
      )}
    </TriggerButton>
  )

  return (
    <Container>
      <UserMenuPopover
        content={content}
        open={open}
        onOpenChange={setOpen}
        side="right"
        align="end"
      >
        {triggerButton}
      </UserMenuPopover>
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  position: relative;
`

const TriggerButton = styled.button<{ $isCollapsed?: boolean }>`
  padding: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '2px 6px')};
  display: flex;
  height: 40px;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  width: ${({ $isCollapsed }) => ($isCollapsed ? '40px' : '100%')};
  border-radius: 6px;
  gap: 12px;
  background: transparent;
  border: none;
  transition:
    width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    padding 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.2s ease,
    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  overflow: hidden;

  &:hover {
    background: #5072a426;
  }

  &:focus {
    outline: none;
  }
`

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  // gap: 2px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

const UserName = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${navDefaultIconColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align: left;
  padding: 0;
`

const UserEmail = styled.div`
  font-size: 10px;
  font-weight: 300;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align: left;
`

const MenuDotsIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${navDefaultIconColor};
  font-size: 16px;
  margin-left: auto;
  flex-shrink: 0;
`

const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
`

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  color: ${navDefaultIconColor};

  &:hover {
    background: #5072a426;
  }
`

const MenuIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
`

const MenuLabel = styled.span`
  font-size: 13px;
  font-weight: 400;
  white-space: nowrap;
`

const UserMenuPopover = styled(Popover)`
  && {
    padding: 6px;
  }
`
